import React from "react";
import {Grid} from "@mui/material";
import {CompPartitionHeader} from "../../../components/PartitionHeader";
import * as PropTypes from "prop-types";
import {ButtonSpacer} from "../../../components/ButtonSpacer";
import {connect} from "react-redux";
import {setUser} from "../user.actions";
import {generateKey, isFunction} from "../../../utils";
import {useTranslation} from "react-i18next";
import {selectButtonType} from "../../../components/utils";
import { CompCheckbox } from "../../../components/Checkbox";
import {CompListTree} from "../../../components/ListTree";
import {getOrganizationTree} from "../../municipality/municipality.actions";
import {
    hasPermission,
    hasPermissionToHandleRole,
    hasRole,
    PERMISSION_INVITE_NEW_ADMIN,
    ROLE_ADMIN_MUNICIPALITY,
    ROLE_ADMIN_SYSTEM,
    ROLE_GUEST
} from "../../../permissions";
import { CompDropdownNew } from "../../../components/newComponents/DropdownNew";
import { CompTextFieldNew } from "../../../components/newComponents/TextField";

const UserFormComponent = ({
                               authenticatedUser,
                               municipalities,
                               organizationTree,
                               getOrganizationTree,
                               userRoles,
                               props
                           }) => {
    const {
        label,
        onChange,
        editorActivated,
        actionButtons,
        selectedUser = {},
        fieldErrors = {},
    } = props;

    const {t} = useTranslation('translation');

    const checkUserRoles = [
        'ROLE_ADMIN_SYSTEM',
        'ROLE_ADMIN_MUNICIPALITY',
        'ROLE_LEADER_MUNICIPALITY'
    ]

    const isGuestUser = hasRole(ROLE_GUEST, authenticatedUser)

    const handleCheckboxChange = (event, value, row) => {
        if (!row.key) {
            console.error("undefined", row);
            return;
        }

        selectedUser[row.key] = event.target.checked;
    
        const data = {
            target: {
                value: selectedUser[row.key],
                name: row.key,
            },
        };
    
        handleChange(data);
    };

    const rows = [
        { 
            key: 'responsiblePerson',
            value: selectedUser.responsiblePerson,
            label: t('user.responsiblePerson'),
            disabled: !editorActivated || !checkUserRoles.includes(selectedUser.role),
            infoText: t('user.responsibleInfotext')
        },
        { 
            key:'contactPerson',
            value: selectedUser.contactPerson,
            label: t('user.contactPerson'),
            disabled: !editorActivated || !checkUserRoles.includes(selectedUser.role),
            infoText: t('user.contactInfotext')
        },
        { 
            key: 'purchaserPerson',
            value: selectedUser.purchaserPerson,
            label: t('user.purchaserPerson'),
            infoText: t('user.purchaserInfotext'),
            disabled: !editorActivated
        }
      ];
    if (selectedUser.entraIdentifier) {
        rows.push({
            key: 'overrideEntra',
            value: selectedUser.overrideEntra,
            label: t('user.override_entra'),
            disabled: !editorActivated && !(
            hasRole(ROLE_ADMIN_SYSTEM, authenticatedUser) ||
                ((hasPermission(ROLE_ADMIN_MUNICIPALITY, authenticatedUser) && authenticatedUser.mainMunicipalityId === selectedUser.mainMunicipalityId))
            )
        })
    }

    if (municipalities) {
        municipalities.sort((a,b) => a.name.localeCompare(b.name));
    }

    const selectOrganization = (row, isAddAction, index, i) => {
        let newSelectedOrganizations = [];
        if (selectedUser.organizations !== undefined && selectedUser.organizations != null) {
            newSelectedOrganizations = [...selectedUser.organizations];
        }

        if (isAddAction) { /* add */
            newSelectedOrganizations.push(row)
        } else { /* remove */
            newSelectedOrganizations.splice(i, 1)
        }

        const data = {
            target: {
                value: newSelectedOrganizations,
                name: 'organizations',
            },
        };
        handleChange(data);
    }

    const handleMunicipalityChange = (event) => {
        getOrganizationTree(event.target.value);
        handleChange(event);
    };

    const handleChange = (event) => {
        if (isFunction(onChange)) {
            onChange(event)
        }
    };

    const handleRoleChange = (event) => {

        if (!checkUserRoles.includes(event.target.value)) {
            selectedUser.responsiblePerson =  false;
            selectedUser.contactPerson = false;
        }

        handleChange(event);
    };


    const handleClick = (event, callback) => {
        event.preventDefault()
        const data = {...selectedUser}
        callback(event, data);
    };

    const getOrganizationIds = (organizations) => {
        if (organizations === undefined || organizations == null) {
            return [];
        }
        return organizations.map(function (organization) {
            return organization.id;
        });
    }


    let componentActionButtons = [];
    if (actionButtons) {
        actionButtons.forEach(function (button) {
            const key = generateKey('userEditButton');
            selectButtonType(button, componentActionButtons, key, handleClick);
        });
    }

    const roles = [];
    if (editorActivated) {
        userRoles.forEach((role) => {
            if(hasPermissionToHandleRole(role, authenticatedUser)) {
                roles.push({
                    id: role,
                    value: t("common." + role)
                })
            }
        })
        if (roles.length === 0) {
            userRoles.forEach((role) => {
                if(role === authenticatedUser.role) {
                    roles.push({
                        id: role,
                        value: t("common." + role)
                    })
                }
            })
        }
    }
    else {
        userRoles.forEach((role) => {
            roles.push({
                id: role,
                value: t("common." + role)
            })
        })
    }

    let municipalityChangeActive = false;
    if (editorActivated && hasPermission(PERMISSION_INVITE_NEW_ADMIN, authenticatedUser)) {
        municipalityChangeActive = true;
    }

    if(!selectedUser.mainMunicipalityId) {
        selectedUser.mainMunicipalityId = authenticatedUser.mainMunicipalityId;
    }

    return (
        <form noValidate autoComplete="off" className={"user-container"}>
            <Grid container spacing={1}>
                <Grid size={12}>
                    <Grid container spacing={0}>
                        <Grid size={12}>
                            <CompPartitionHeader text={label}/>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid sx={{pr: '11px'}} size={{ xs: 12, sm: 3 }}>
                    <CompTextFieldNew
                        label={`${t("user.first_name")}*`}
                        isLabelShrink={true}
                        value={selectedUser.firstName}
                        onChange={handleChange}
                        disabled={!editorActivated || (selectedUser.entraIdentifier && !selectedUser.overrideEntra)}
                        error={fieldErrors.firstName}
                        name="firstName"
                    />
                </Grid>
                <Grid sx={{pr: '11px'}} size={{ xs: 12, sm: 3 }}>
                    <CompTextFieldNew
                        label={`${t("user.last_name")}*`}
                        isLabelShrink={true}
                        value={selectedUser.lastName}
                        onChange={handleChange}
                        disabled={!editorActivated || (selectedUser.entraIdentifier && !selectedUser.overrideEntra)}
                        error={fieldErrors.lastName}
                        name="lastName"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <CompTextFieldNew
                        label={t("user.title")}
                        isLabelShrink={true}
                        value={selectedUser.title}
                        onChange={handleChange}
                        disabled={!editorActivated}
                        error={fieldErrors.title}
                        name="title"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {!isGuestUser && (
                        <CompDropdownNew
                            label={t("user.user_role")}
                            valueColumn={"id"}
                            isLabelShrink={true}
                            labelColumn={"value"}
                            rows={roles}
                            error={fieldErrors.userRole}
                            hasEmptyOption={false}
                            onChange={handleRoleChange}
                            userInfo={true}
                            disabled={!editorActivated || (selectedUser.entraIdentifier && !selectedUser.overrideEntra)}
                            name="role"
                            selectedValue={selectedUser.role}
                        />
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {!isGuestUser && (
                        <CompDropdownNew
                            label={`${t("common.municipality")}*`}
                            isLabelShrink={true}
                            valueColumn={"id"}
                            labelColumn={"name"}
                            rows={municipalities}
                            error={fieldErrors.mainMunicipalityId}
                            hasEmptyOption={false}
                            onChange={handleMunicipalityChange}
                            disabled={!municipalityChangeActive || selectedUser.entraIdentifier}
                            name="mainMunicipalityId"
                            selectedValue={selectedUser.mainMunicipalityId?selectedUser.mainMunicipalityId: null}
                        />
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {!isGuestUser && (
                        <CompCheckbox
                            rows={rows}
                            error={fieldErrors.roles}
                            selected={selectedUser}
                            name="roleCheckbox"
                            className="roles"
                            onChange={handleCheckboxChange}
                            booleanmode={true}
                            infoMode={true}
                        />
                    )}
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {  selectedUser.entraIdentifier ?
                        <CompTextFieldNew
                            isLabelShrink={true}
                            value={selectedUser.entraIdentifier}
                            disabled={true}
                            name="entra_id"
                            type={'entra_id'}
                            label={`${t("user.entra_id")}*`}
                        /> :
                        <CompTextFieldNew
                            isLabelShrink={true}
                            value={selectedUser.email}
                            onChange={handleChange}
                            disabled={!editorActivated}
                            name="email"
                            error={fieldErrors.email}
                            type={'email'}
                            label={`${t("user.email")}*`}
                        />
                    }
                </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <CompListTree
                            onSelect={selectOrganization}
                            rows={organizationTree[3]}
                            selectedRows={getOrganizationIds(selectedUser.organizations)}
                            disabled={!editorActivated}
                            error={fieldErrors.organizations}
                            name="organizations"
                            wholeTreeOpen={true}
                            keyColumn={'id'}
                            valueColumn={'name'}
                        >
                            {t('user.organizations')}
                        </CompListTree>
                    </Grid>
            </Grid>
            <Grid container>
                <Grid className={'align-right'} size={12}>
                    <ButtonSpacer>
                        {componentActionButtons}
                    </ButtonSpacer>
                </Grid>
            </Grid>
        </form>
    );
}

UserFormComponent.propTypes = {
    label: PropTypes.string,
    editorActivated: PropTypes.bool,
    onChange: PropTypes.func,
    selectedUser: PropTypes.object,
    fieldErrors: PropTypes.object,
    actionButtons: PropTypes.array,
};

const mapStateToProps = (state, ownProps) => {
    // return state;
    return {
        authenticatedUser: state.auth.authenticatedUser,
        municipalities: state.municipality.municipalities,
        organizationTree: state.municipality.organizationTree,
        userRoles: state.root.userRoles,
        props: ownProps
    }
}
const mapDispatchToProps = dispatch => {
    return {
        setUser: (data) => dispatch(setUser(data)),
        getOrganizationTree: (municipalityId, level) => dispatch(getOrganizationTree(municipalityId, level)),
    };
}

const UserForm = connect(mapStateToProps, mapDispatchToProps)(UserFormComponent);

export {UserForm};